import {api} from '@/shared/services/api-client';
import {
  fetchAllSchedules,
  fetchSchedulesPage,
  type ScheduleListParams,
} from '@/shared/services/scheduleRetrieval';
import type {BulkInviteScopeFilters} from '@/shared/types/bulkInvite';

export type {BulkInviteScopeFilters} from '@/shared/types/bulkInvite';

/** @deprecated Use `ScheduleListParams` from shared/services/scheduleRetrieval. */
export type SchedulerListParams = ScheduleListParams;

/** Query params for the server-side Scheduler queue endpoint (GET /scheduler/queue). */
export interface SchedulerQueueParams {
  project_id: string;
  queue?: 'all' | 'my' | 'unassigned' | 'upcoming' | 'group_booking';
  scheduler_id?: string;
  page?: number;
  limit?: number;
  search?: string;
  /** CSV strings (comma-joined sets). */
  schedule_statuses?: string;
  genders?: string;
  project_statuses?: string;
  assigned_schedulers?: string;
  session_statuses?: string;
  session_status_card?: string;
  session_types?: string;
  session_names?: string;
  family_codes?: string;
  unassigned_moderator_only?: boolean;
  /** CSV of selected real moderator names (independent of unassigned_moderator_only). */
  assigned_moderators?: string;
  sort_column?: string;
  sort_direction?: 'asc' | 'desc';
  group_booking_time_filter?: string;
  /** Force a fresh server load (after mutations / on (re)mount), bypassing the cache. */
  refresh?: boolean;
}

/**
 * Query params for the server-side Booked Sessions list (GET /scheduler/booked-queue).
 * Mirrors the FE pipeline inputs: page-level filters (from Schedule.tsx) + the
 * BookedSessions in-component filter/search/sort/tab/page state.
 */
export interface BookedQueueParams {
  project_id: string;
  page?: number;
  sessionTimeTab?: 'upcoming' | 'past' | 'cancelled';
  /** Page-level filters (Schedule.tsx) — CSV strings. moderators resolved to user_ids. */
  filterSessionTypes?: string;
  filterSessionNames?: string;
  filterLocations?: string;
  filterModeratorUserIds?: string;
  filterStatuses?: string;
  /** Added-column filters (Gender, Ethnicity, ...) — JSON string of Record<columnId, string[]>. */
  filterColumnFilters?: string;
  /** BookedSessions in-component filters. */
  selectedSessionType?: string;
  selectedMeetingPoint?: string;
  selectedModerator?: string;
  selectedStatus?: string;
  isModerator?: boolean;
  currentModeratorIds?: string;
  inlineDateMode?: 'none' | 'next24' | 'range';
  inlineRangeStart?: string;
  inlineRangeEnd?: string;
  viewerTimeZone?: string;
  searchQuery?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  /** Force a fresh server load (after mutations / on mount), bypassing the cache. */
  refresh?: boolean;
}

/** Bulk invite Step 1 — one card per active session type. */
/** One venue a session type can run at (OD-1325). */
export interface SessionTypeMeetingPoint {
  location_id: number;
  name: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  timezone: string | null;
  is_primary: boolean;
}

/** Full address on one line, as shown under the invite modal's dropdown. */
export const formatMeetingPointAddress = (mp: SessionTypeMeetingPoint): string =>
  [mp.street_address, mp.city, mp.state, mp.zip_code, mp.country]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(', ');

export interface InvitableSessionType {
  session_type_id: string;
  name: string;
  location_type: string | null;
  has_moderators: boolean;
  pending_count: number;
  /**
   * Active meeting points, primary first. Empty for a session type with none
   * configured — the invite step blocks on that rather than sending an invite
   * with no address.
   */
  meeting_points?: SessionTypeMeetingPoint[];
}

/** Bulk invite Step 2 — one row per participant for the chosen session type. */
export interface InvitableParticipant {
  participant_id: string;
  name: string;
  email: string | null;
  participant_status: string | null;
  session_status: string | null;
  /** True when the participant already holds an active, unused booking link. */
  has_active_link: boolean;
  /** OD-1139: Total number of (re)invites OR scheduler-led (re)bookings for this participant/session type. */
  invite_count: number;
  eligible: boolean;
  ineligible_reason: string | null;
}

export const schedulerApi = {
  /** Server-side queue: filter/sort/paginate happen on the backend. */
  queue: (params: SchedulerQueueParams) =>
    api.scheduler.queue(params as unknown as Record<string, unknown>),

  /** Bulk invite Step 1: active session types + count of invitable participants. */
  listInvitableSessionTypes: (projectId: string, filters?: BulkInviteScopeFilters) =>
    api.scheduler.sessionTypesInvitable({project_id: projectId, ...filters}),

  /** Bulk invite Step 2: invitable participants for one session type (server-side search + pagination). */
  listInvitableParticipants: (
    sessionTypeId: string,
    projectId: string,
    opts?: {search?: string; page?: number; limit?: number; filters?: BulkInviteScopeFilters},
  ) =>
    api.scheduler.sessionTypeInvitableParticipants(sessionTypeId, {
      project_id: projectId,
      ...(opts?.search?.trim() ? {search: opts.search.trim()} : {}),
      ...(opts?.page ? {page: opts.page} : {}),
      ...(opts?.limit ? {limit: opts.limit} : {}),
      ...opts?.filters,
    }),

  /** Server-side Booked Sessions list: filter/sort/search/paginate on the backend. */
  bookedQueue: (params: BookedQueueParams) =>
    api.scheduler.bookedQueue(params as unknown as Record<string, unknown>),

  /** Server-side Booked Sessions KPI tiles (page-level filters only). */
  bookedStats: (params: {
    project_id: string;
    filterSessionTypes?: string;
    filterSessionNames?: string;
    filterLocations?: string;
    filterModeratorUserIds?: string;
    filterStatuses?: string;
    filterColumnFilters?: string;
    refresh?: boolean;
  }) => api.scheduler.bookedStats(params as unknown as Record<string, unknown>),

  listModerators: () => api.moderators.list(),
  listActiveModerators: () => api.moderators.getActive(),
  getModeratorByEmail: (email: string) => api.moderators.getByEmail(email),
  getModeratorsByIds: (ids: string[]) => api.moderators.getByIds(ids),
  updateModerator: (id: string, data: any) => api.moderators.update(id, data),
  registerModerator: (data: any) => api.moderators.register(data),
  deleteModeratorPermanent: (id: string) => api.moderators.deletePermanent(id),

  listSchedules: (params?: ScheduleListParams) =>
    fetchSchedulesPage(params, 'schedulerApi.listSchedules'),
  listAllSchedules: (params?: ScheduleListParams, pageSize?: number) =>
    fetchAllSchedules(params, pageSize),
  /**
   * Session Report export. Pass the active Scheduler queue filters (same shape as
   * `queue`) so the export mirrors the on-screen grid; omit them to export the
   * full project. The backend only reads the filter keys (page/limit/sort ignored).
   */
  consolidatedExport: (params?: Partial<SchedulerQueueParams> & { project_id?: string }) =>
    api.schedules.consolidatedExport(params),
  /**
   * Contact Log export. Pass the active Scheduler queue filters (same shape as
   * `queue`) so the export mirrors the on-screen grid; omit them to export the
   * full project. The backend only reads the filter keys (page/limit/sort ignored).
   */
  contactLogExport: (params?: Partial<SchedulerQueueParams> & { project_id?: string }) =>
    api.schedules.contactLogExport(params),
  getSchedule: (id: string) => api.schedules.get(id),
  getSchedulesByParticipant: (participantId: string) =>
    api.schedules.getByParticipant(participantId),
  createSchedule: (data: any) => api.schedules.create(data),
  assignGroupBookingId: (data: {project_id: string; schedule_ids: string[]}) =>
    api.schedules.assignGroupId(data),
  createSimpleSchedule: (data: any) => api.schedules.createSimple(data),
  updateSchedule: (id: string, data: any) => api.schedules.update(id, data),
  updateActualTimes: (
    id: string,
    data: {
      actual_start_time?: string;
      actual_end_time?: string;
      reason: string;
    },
  ) => api.schedules.updateActualTimes(id, data),
  /**
   * OD Bulk Cancel. Either the full payment form (payment + category +
   * future-booking), or — while the payment options are flagged off — a plain
   * `cancellation_type` + reason, which cancels without any payment routing.
   */
  bulkCancel: (
    data:
      | {
          project_id: string;
          schedule_ids: string[];
          payment: 'with' | 'without';
          category?: 'overbooking' | 'paused' | 'other';
          explanation: string;
          allow_future_booking: boolean;
        }
      | {
          project_id: string;
          schedule_ids: string[];
          cancellation_type: 'With Partial Payment' | 'Without Partial Payment';
          explanation: string;
        },
  ) => api.schedules.bulkCancel(data),
  /**
   * OD-1277 Group Booking cancellation. Same two payload shapes as
   * `bulkCancel` (full payment form, or reason-only while the payment options
   * are flagged off), scoped to one booking group. Selecting every cancellable
   * participant is a full group cancellation; selecting a subset removes just
   * those participants.
   */
  cancelGroupBooking: (
    bookingGroupId: string,
    data:
      | {
          project_id: string;
          schedule_ids: string[];
          payment: 'with' | 'without';
          category?: string;
          explanation: string;
          allow_future_booking: boolean;
        }
      | {
          project_id: string;
          schedule_ids: string[];
          cancellation_type: 'With Partial Payment' | 'Without Partial Payment';
          explanation: string;
        },
  ) => api.schedules.groupCancel(bookingGroupId, data),
  disqualifySchedule: (id: string, data: { reason: string }) =>
    api.schedules.disqualify(id, data),
  doNotHireSchedule: (id: string, data: { reason: string }) =>
    api.schedules.doNotHire(id, data),
  overbookSchedule: (id: string, data: { reason: string }) =>
    api.schedules.overbook(id, data),
  correctScheduleStatus: (
    id: string,
    data: {corrected_status: string; reason: string},
  ) => api.schedules.correctStatus(id, data),
  deleteSchedule: (id: string) => api.schedules.delete(id),

  getAvailableModerators: (projectId: string) =>
    api.schedules.getAvailableModerators(projectId),
  getAvailableSlots: (moderatorId: string, projectId: string, params?: any) =>
    api.schedules.getAvailableSlots(moderatorId, projectId, params),
  participantBook: (data: any) => api.schedules.participantBook(data),
  participantCancel: (scheduleId: string, data: any) =>
    api.schedules.participantCancel(scheduleId, data),
  participantReschedule: (scheduleId: string, data: any) =>
    api.schedules.participantReschedule(scheduleId, data),

  generateCheckInCode: (scheduleId: string) =>
    api.schedules.generateCheckInCode(scheduleId),
  verifyCheckInCode: (scheduleId: string, data: any) =>
    api.schedules.verifyCheckInCode(scheduleId, data),
  getCheckInStatus: (scheduleId: string) => api.schedules.getCheckInStatus(scheduleId),
  getParticipantMeetings: (participantId: string, params?: any) =>
    api.schedules.getParticipantMeetings(participantId, params),
  addScheduleNote: (
    scheduleId: string,
    data: {body: string; authorName?: string; type?: string},
  ) => api.schedules.addNote(scheduleId, data),

  listModeratorAvailability: (params?: {approved_only?: boolean; status?: string}) =>
    api.moderatorAvailability.list(params),
  getModeratorAvailabilityByModerator: (moderatorId: string, approvedOnly?: boolean) =>
    api.moderatorAvailability.getByModerator(moderatorId, approvedOnly),
  createModeratorAvailability: (data: any) => api.moderatorAvailability.create(data),
  createModeratorAvailabilityBulk: (data: any[]) =>
    api.moderatorAvailability.bulkCreate(data),
  updateModeratorAvailabilityPeriod: (moderatorId: string, data: any) =>
    api.moderatorAvailability.updatePeriod(moderatorId, data),
  updateModeratorAvailability: (id: string, data: any) =>
    api.moderatorAvailability.update(id, data),
  deleteModeratorAvailability: (id: string) => api.moderatorAvailability.delete(id),
  checkModeratorAvailabilityDelete: (availabilityIds: string[]) =>
    api.moderatorAvailability.checkDelete(availabilityIds),
  deleteModeratorAvailabilityByModerator: (moderatorId: string) =>
    api.moderatorAvailability.deleteByModerator(moderatorId),
  approveModeratorAvailability: (id: string, displayTimezone?: string) =>
    api.moderatorAvailability.approve(id, displayTimezone),
  requestDeleteModeratorAvailability: (id: string) =>
    api.moderatorAvailability.requestDelete(id),
  rejectModeratorAvailability: (id: string, displayTimezone?: string) =>
    api.moderatorAvailability.reject(id, displayTimezone),

  listScheduleLocations: () => api.scheduleLocations.list(),
  getScheduleLocation: (id: string) => api.scheduleLocations.get(id),
  getScheduleLocationsByProject: (projectId: string) =>
    api.scheduleLocations.getByProject(projectId),
  createScheduleLocation: (data: any) => api.scheduleLocations.create(data),
  updateScheduleLocation: (id: string, data: any) =>
    api.scheduleLocations.update(id, data),
  deleteScheduleLocation: (id: string) => api.scheduleLocations.delete(id),
  toggleScheduleLocation: (id: string) => api.scheduleLocations.toggle(id),

  /**
   * Group Booking wizard Step 2: participants a scheduler can book directly
   * into this session type (server-side search + pagination).
   *
   * Same endpoint as Bulk Invite's Step 2 but with `mode=booking`, which drops
   * the invite-only blockers (outstanding self-booking link, missing email) and
   * applies the re-bookable statuses the Book a Session page enforces. Using the
   * plain participants list here instead would offer participants who already
   * hold a session of this type, and Book a Session would silently drop them.
   */
  listBookableParticipants: (
    sessionTypeId: string,
    projectId: string,
    opts?: {search?: string; page?: number; limit?: number},
  ) =>
    api.scheduler.sessionTypeInvitableParticipants(sessionTypeId, {
      project_id: projectId,
      mode: 'booking',
      ...(opts?.search?.trim() ? {search: opts.search.trim()} : {}),
      ...(opts?.page ? {page: opts.page} : {}),
      ...(opts?.limit ? {limit: opts.limit} : {}),
    }),

  listSessionTypes: () => api.sessionTypes.list(),
  getSessionType: (id: string) => api.sessionTypes.get(id),
  listDetailedSessionTypes: () => api.sessionTypes.detailed(),
  listDetailedSessionTypesByProject: (projectId: string) =>
    api.sessionTypes.detailedByProject(projectId),
  listSessionTypesByProject: (projectId: string) =>
    api.sessionTypes.getByProject(projectId),
  participantJobAssignmentsByProject: (projectId: string) =>
    api.sessionTypes.participantJobAssignmentsByProject(projectId),
  jobsByProject: (projectId: string) =>
    api.sessionTypes.jobsByProject(projectId),
  createSessionType: (data: any) => api.sessionTypes.create(data),
  updateSessionType: (id: string, data: any) => api.sessionTypes.update(id, data),
  deleteSessionType: (id: string) => api.sessionTypes.delete(id),
  toggleSessionType: (id: string) => api.sessionTypes.toggle(id),
};
